import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import ImportCSV from '../import/ImportCSV';
import { supabase } from '../../supabase/client';
import { useNotification } from '../../context/NotificationContext';

const { FiUsers, FiGrid, FiList, FiSearch, FiX, FiUploadCloud, FiChevronDown, FiPlus, FiPhone, FiMail, FiMapPin } = FiIcons;

// Mock data for development
const MOCK_PROVIDERS = [
  {
    id: '1',
    name: 'Dr. John Smith',
    specialty: 'General Dentist',
    location: 'New York, NY',
    phone: '(555) 123-4567',
    email: 'john.smith@example.com',
    practice_name: 'Smith Dental Care',
    practice_address: '123 Main St, New York, NY 10001',
    referral_count: 150,
    availability: 'Available',
    bio: 'Experienced general dentist with over 15 years of practice, specializing in restorative and cosmetic dentistry.',
    created_at: new Date().toISOString()
  },
  {
    id: '2',
    name: 'Dr. Sarah Johnson',
    specialty: 'Orthodontist',
    location: 'Los Angeles, CA',
    phone: '(555) 987-6543',
    email: 'sarah.johnson@example.com',
    practice_name: 'Johnson Orthodontics',
    practice_address: '456 Oak Ave, Los Angeles, CA 90210',
    referral_count: 89,
    availability: 'Busy',
    bio: 'Specialized in pediatric orthodontics and Invisalign treatments with 12 years of experience.',
    created_at: new Date().toISOString()
  },
  {
    id: '3',
    name: 'Dr. Michael Chen',
    specialty: 'Oral Surgeon',
    location: 'Chicago, IL',
    phone: '(555) 456-7890',
    email: 'michael.chen@example.com',
    practice_name: 'Chen Oral Surgery',
    practice_address: '789 Pine St, Chicago, IL 60601',
    referral_count: 234,
    availability: 'Available',
    bio: 'Board-certified oral and maxillofacial surgeon with expertise in dental implants and complex extractions.',
    created_at: new Date().toISOString()
  },
  {
    id: '4',
    name: 'Dr. Emily Rodriguez',
    specialty: 'Periodontist',
    location: 'Houston, TX',
    phone: '(555) 321-0987',
    email: 'emily.rodriguez@example.com',
    practice_name: 'Rodriguez Periodontics',
    practice_address: '321 Elm St, Houston, TX 77001',
    referral_count: 167,
    availability: 'Available',
    bio: 'Periodontal specialist focusing on gum disease treatment and dental implant placement.',
    created_at: new Date().toISOString()
  },
  {
    id: '5',
    name: 'Dr. David Wilson',
    specialty: 'Endodontist',
    location: 'Phoenix, AZ',
    phone: '(555) 654-3210',
    email: 'david.wilson@example.com',
    practice_name: 'Wilson Endodontics',
    practice_address: '654 Maple Ave, Phoenix, AZ 85001',
    referral_count: 98,
    availability: 'Limited',
    bio: 'Root canal specialist with advanced training in microscopic endodontics.',
    created_at: new Date().toISOString()
  },
  {
    id: '6',
    name: 'Dr. Lisa Thompson',
    specialty: 'Pediatric Dentist',
    location: 'Philadelphia, PA',
    phone: '(555) 789-0123',
    email: 'lisa.thompson@example.com',
    practice_name: 'Kids Dental Care',
    practice_address: '987 Cedar Rd, Philadelphia, PA 19101',
    referral_count: 203,
    availability: 'Available',
    bio: 'Pediatric dentist dedicated to creating positive dental experiences for children and teens.',
    created_at: new Date().toISOString()
  }
];

const ProviderCard = ({ provider }) => (
  <motion.div
    layout
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.8 }}
    transition={{ duration: 0.3 }}
    className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300"
  >
    <div className="p-6">
      <div className="flex items-center mb-4">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mr-4">
          <SafeIcon icon={FiUsers} className="text-2xl text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-800 dark:text-white">{provider.name}</h3>
          <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">{provider.specialty}</p>
          {provider.practice_name && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{provider.practice_name}</p>
          )}
        </div>
      </div>

      {provider.bio && (
        <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-3">{provider.bio}</p>
      )}

      <div className="space-y-2 mb-4">
        {provider.phone && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <SafeIcon icon={FiPhone} className="mr-2 text-blue-500" />
            <span>{provider.phone}</span>
          </div>
        )}
        {provider.email && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <SafeIcon icon={FiMail} className="mr-2 text-blue-500" />
            <span className="truncate">{provider.email}</span>
          </div>
        )}
        {provider.location && (
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <SafeIcon icon={FiMapPin} className="mr-2 text-blue-500" />
            <span className="truncate">{provider.location}</span>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <div className="flex justify-between items-center">
          {provider.referral_count !== null && (
            <div className="text-sm">
              <span className="text-gray-500 dark:text-gray-400">Referrals: </span>
              <span className="font-semibold text-gray-800 dark:text-white">{provider.referral_count}</span>
            </div>
          )}
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
            provider.availability === 'Available' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
              : provider.availability === 'Busy'
              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
          }`}>
            {provider.availability || 'Available'}
          </span>
        </div>
      </div>
    </div>
  </motion.div>
);

const ProviderRow = ({ provider }) => (
  <motion.tr
    layout
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
  >
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="flex items-center">
        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
          <SafeIcon icon={FiUsers} className="text-lg text-white" />
        </div>
        <div className="ml-4">
          <div className="text-sm font-medium text-gray-900 dark:text-white">{provider.name}</div>
          {provider.practice_name && (
            <div className="text-sm text-gray-500 dark:text-gray-400">{provider.practice_name}</div>
          )}
        </div>
      </div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="text-sm text-gray-900 dark:text-white">{provider.specialty}</div>
      <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{provider.location}</div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
        provider.availability === 'Available'
          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
          : provider.availability === 'Busy'
          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      }`}>
        {provider.availability || 'Available'}
      </span>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <div className="text-sm text-gray-900 dark:text-white">{provider.phone}</div>
      <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">{provider.email}</div>
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
      {provider.referral_count || 0}
    </td>
  </motion.tr>
);

const ProviderDirectory = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSpecialty, setFilterSpecialty] = useState('All');
  const [modalOpen, setModalOpen] = useState(false);
  const { addNotification } = useNotification();

  const fetchProviders = async () => {
    setLoading(true);
    
    if (!supabase) {
      // Use mock data when Supabase is not available
      console.log('Using mock data - Supabase not configured');
      setTimeout(() => {
        setProviders(MOCK_PROVIDERS);
        setLoading(false);
        addNotification('Demo mode - Using sample provider data', 'info');
      }, 1000); // Simulate loading time
      return;
    }

    try {
      console.log('Fetching providers from Supabase...');
      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching providers:', error);
        addNotification(`Database error: ${error.message}`, 'error');
        // Fall back to mock data on error
        setProviders(MOCK_PROVIDERS);
        addNotification('Switched to demo data due to database error', 'warning');
      } else {
        console.log('Successfully fetched providers:', data?.length || 0);
        setProviders(data || []);
        if (data && data.length > 0) {
          addNotification(`Loaded ${data.length} providers from database`, 'success');
        }
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      addNotification('Connection error - using demo data', 'warning');
      setProviders(MOCK_PROVIDERS);
    }
    
    setLoading(false);
  };
  
  useEffect(() => {
    fetchProviders();
  }, []);

  const specialties = useMemo(() => {
    const uniqueSpecialties = [...new Set(providers.map(p => p.specialty))].filter(Boolean);
    return ['All', ...uniqueSpecialties];
  }, [providers]);

  const filteredProviders = useMemo(() => {
    return providers.filter(provider => {
      const nameMatch = provider.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       provider.practice_name?.toLowerCase().includes(searchTerm.toLowerCase());
      const specialtyMatch = filterSpecialty === 'All' || provider.specialty === filterSpecialty;
      return nameMatch && specialtyMatch;
    });
  }, [providers, searchTerm, filterSpecialty]);

  const handleDataImported = async (importedData) => {
    if (!supabase) {
      addNotification('Database not available. Import saved locally for demo.', 'warning');
      
      // Simulate adding to local data for demo
      const newProviders = importedData.map((item, index) => ({
        id: `imported-${Date.now()}-${index}`,
        name: item.Name || item.name || '',
        phone: item.Phone || item.phone || '',
        email: item.Email || item.email || '',
        practice_name: item['Practice Name'] || item.practice_name || '',
        practice_address: item['Practice Address'] || item.practice_address || '',
        specialty: item.Specialty || item.specialty || 'General Dentist',
        referral_count: parseInt(item['Referral Count'] || item.referral_count || '0') || 0,
        birthday: item.Birthday || item.birthday || '',
        last_referral_date: item['Last Referral Date'] || item.last_referral_date || null,
        location: item['Practice Address'] || item.practice_address || item.Location || item.location || '',
        availability: 'Available',
        bio: `${item.Specialty || item.specialty || 'Healthcare'} provider at ${item['Practice Name'] || item.practice_name || 'Private Practice'}`,
        created_at: new Date().toISOString()
      })).filter(item => item.name && item.name.trim() !== '');

      setProviders(prev => [...prev, ...newProviders]);
      addNotification(`Added ${newProviders.length} providers to demo data`, 'success');
      setModalOpen(false);
      return;
    }
    
    try {
      // Transform the imported data to match our database schema
      const transformedData = importedData.map(item => ({
        name: item.Name || item.name || '',
        phone: item.Phone || item.phone || '',
        email: item.Email || item.email || '',
        practice_name: item['Practice Name'] || item.practice_name || '',
        practice_address: item['Practice Address'] || item.practice_address || '',
        specialty: item.Specialty || item.specialty || 'General Dentist',
        referral_count: parseInt(item['Referral Count'] || item.referral_count || '0') || 0,
        birthday: item.Birthday || item.birthday || '',
        last_referral_date: item['Last Referral Date'] || item.last_referral_date || null,
        location: item['Practice Address'] || item.practice_address || item.Location || item.location || '',
        availability: 'Available',
        bio: `${item.Specialty || item.specialty || 'Healthcare'} provider at ${item['Practice Name'] || item.practice_name || 'Private Practice'}`
      })).filter(item => item.name && item.name.trim() !== '');

      if (transformedData.length === 0) {
        addNotification('No valid provider data found in the import file', 'error');
        setModalOpen(false);
        return;
      }

      console.log('Importing providers to database:', transformedData.length);
      const { data, error } = await supabase
        .from('providers')
        .insert(transformedData)
        .select();
      
      if (error) {
        console.error('Import error:', error);
        addNotification(`Error importing data: ${error.message}`, 'error');
      } else {
        console.log('Successfully imported providers:', data?.length);
        addNotification(`Successfully imported ${data.length} providers!`, 'success');
        fetchProviders(); // Refresh the list
      }
    } catch (err) {
      console.error('Import error:', err);
      addNotification('Error processing import data', 'error');
    }
    
    setModalOpen(false);
  };

  return (
    <div className="p-4 md:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <ImportCSV 
                onClose={() => setModalOpen(false)} 
                onDataImported={handleDataImported} 
                recordType="providers"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <header className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-white">Provider Directory</h1>
            <p className="text-lg text-gray-500 dark:text-gray-400 mt-1">
              Search, filter, and manage healthcare providers. ({providers.length} providers)
            </p>
            {!supabase && (
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-1 flex items-center">
                <SafeIcon icon={FiUsers} className="mr-1" />
                Demo Mode - Sample data shown
              </p>
            )}
          </div>
          <div className="flex items-center space-x-2 mt-4 md:mt-0">
            <button
              onClick={() => setModalOpen(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-all duration-200 flex items-center"
            >
              <SafeIcon icon={FiUploadCloud} className="mr-2"/>
              Import Providers
            </button>
            <button
              onClick={() => addNotification('Add Provider feature coming soon!', 'info')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-all duration-200 flex items-center"
            >
              <SafeIcon icon={FiPlus} className="mr-2"/>
              Add Provider
            </button>
          </div>
        </div>
      </header>

      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative md:col-span-1">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or practice..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-200"
            />
            {searchTerm && (
              <SafeIcon 
                icon={FiX} 
                onClick={() => setSearchTerm('')} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer hover:text-gray-600 dark:hover:text-gray-300" 
              />
            )}
          </div>
          <div className="relative md:col-span-1">
            <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select
              value={filterSpecialty}
              onChange={(e) => setFilterSpecialty(e.target.value)}
              className="w-full appearance-none px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-200"
            >
              {specialties.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-end md:col-span-1">
            <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button 
                onClick={() => setViewMode('grid')} 
                className={`p-2 rounded-md transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-white dark:bg-gray-800 shadow text-indigo-600 dark:text-indigo-400' 
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <SafeIcon icon={FiGrid} />
              </button>
              <button 
                onClick={() => setViewMode('list')} 
                className={`p-2 rounded-md transition-all ${
                  viewMode === 'list' 
                    ? 'bg-white dark:bg-gray-800 shadow text-indigo-600 dark:text-indigo-400' 
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <SafeIcon icon={FiList} />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="flex flex-col justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading providers...</p>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={viewMode}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <AnimatePresence>
                  {filteredProviders.map(provider => (
                    <ProviderCard key={provider.id} provider={provider} />
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Provider
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Specialty & Location
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Contact
                      </th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Referrals
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <AnimatePresence>
                      {filteredProviders.map(provider => (
                        <ProviderRow key={provider.id} provider={provider} />
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            )}
            {filteredProviders.length === 0 && !loading && (
              <div className="text-center py-16 text-gray-500 dark:text-gray-400">
                <SafeIcon icon={FiUsers} className="mx-auto h-16 w-16 mb-4 text-gray-300" />
                <p className="text-xl mb-2">No providers found</p>
                <p>Try adjusting your search or filters, or import some providers to get started.</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};

export default ProviderDirectory;